import { ChangeEvent, ReactElement } from 'react'
import GridOnIcon from '@mui/icons-material/GridOn'
import { InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { TooltipIconButton } from '@ui/feedback'
import { MAX_REPLACE_DECIMALS_ROUNDING, MIN_REPLACE_DECIMALS_ROUNDING } from '../constants'
import { useReplaceDecimalRounding } from '../context/ReplaceDecimalRoundingContext'
import { PointManipulationConfig } from '../types'

const clampReplaceDecimals = (value: number): number =>
  Math.min(MAX_REPLACE_DECIMALS_ROUNDING, Math.max(MIN_REPLACE_DECIMALS_ROUNDING, value))

interface DefaultReplaceNumDecimalRoundingActionProps {
  pointManipulation: PointManipulationConfig
}

const DefaultReplaceNumDecimalRoundingAction = ({
  pointManipulation,
}: DefaultReplaceNumDecimalRoundingActionProps): ReactElement => {
  const {
    axisState,
    roundOnReplaceScope,
    setRoundOnReplaceEnabled,
    setRoundOnReplaceScope,
    setAllAxesNumDecimalsRoundingOnReplace,
  } = useReplaceDecimalRounding()

  const visibleAxes = (['x', 'y', 'z'] as const).filter(axis => !pointManipulation[axis].hidden)
  const roundOnReplace = axisState.x.roundOnReplace || axisState.y.roundOnReplace
  const showScopeToggle = roundOnReplace && !pointManipulation.z.hidden
  const numDecimalsRoundingOnReplace =
    visibleAxes.length > 0
      ? axisState[visibleAxes[0]].numDecimalsRoundingOnReplace
      : MIN_REPLACE_DECIMALS_ROUNDING

  const handleNumDecimalsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const parsedValue = Number.parseInt(event.target.value, 10)
    if (!Number.isFinite(parsedValue)) return
    setAllAxesNumDecimalsRoundingOnReplace(clampReplaceDecimals(parsedValue))
  }

  const handleScopeToggle = (): void => {
    setRoundOnReplaceScope(roundOnReplaceScope === 'xy' ? 'xyz' : 'xy')
  }

  if (visibleAxes.length === 0) {
    return <></>
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TooltipIconButton
        tooltip={
          roundOnReplace
            ? 'Koordinaten werden beim Auswählen des Punktes aus der Scene gerundet'
            : 'Koordinaten werden beim Auswählen des Punktes aus der Scene nicht gerundet'
        }
        onClick={() => setRoundOnReplaceEnabled(!roundOnReplace)}
        size="small"
        tooltipProps={{ placement: 'bottom' }}
        sx={{ padding: 0 }}
      >
        <GridOnIcon fontSize="small" color={roundOnReplace ? 'success' : 'inherit'} />
      </TooltipIconButton>
      {showScopeToggle && (
        <TooltipIconButton
          tooltip={
            roundOnReplaceScope === 'xy'
              ? 'Nur X- und Y-Koordinaten werden gerundet (klicken für XYZ)'
              : 'X-, Y- und Z-Koordinaten werden gerundet (klicken für XY)'
          }
          onClick={handleScopeToggle}
          size="small"
          tooltipProps={{ placement: 'bottom' }}
          sx={{ padding: 0 }}
        >
          <Typography>{roundOnReplaceScope.toUpperCase()}</Typography>
        </TooltipIconButton>
      )}
      {roundOnReplace && (
        <TextField
          size="small"
          type="number"
          value={numDecimalsRoundingOnReplace}
          onChange={handleNumDecimalsChange}
          variant="standard"
          inputProps={{
            min: MIN_REPLACE_DECIMALS_ROUNDING,
            max: MAX_REPLACE_DECIMALS_ROUNDING,
            step: 1,
            style: { padding: 0 },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">Dez.</InputAdornment>,
          }}
          sx={{ width: 60 }}
        />
      )}
    </Stack>
  )
}

export default DefaultReplaceNumDecimalRoundingAction
